import { ChangeDetectionStrategy, Component, input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ButtonComponent, IconComponent, InputComponent, MultilineInputComponent, ResolvableModal } from '@softline/ui-core';
import { DateInputComponent, TimeInputComponent } from '@softline/ui-core/date-input';
import { addDays, IsoDate } from '@softline/core';
import { RosterDeliveryResourceType } from '../../types/roster-delivery-resource-type';

export interface EventDialogResult {
  datum: string;
  vonzeit: string;
  biszeit: string;
  bemerkung: string;
  tour: string;
  title: string;
}

@Component({
  selector: 'soft-event-dialog',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    DateInputComponent,
    IconComponent,
    InputComponent,
    MultilineInputComponent,
    TimeInputComponent,
  ],
  templateUrl: './event-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class EventDialogComponent
  extends ResolvableModal<EventDialogResult | 'CANCEL' | 'DELETE'>
  implements OnInit
{
  readonly datum = input<string>('');
  readonly resourceName = input<string>('');
  readonly resourceType = input<RosterDeliveryResourceType | null>(null);
  readonly mode = input<'create' | 'edit'>('create');
  readonly vonzeit = input<string>('');
  readonly biszeit = input<string>('');
  readonly bemerkung = input<string>('');
  readonly tour = input<string>('');
  readonly title = input<string>('');

  protected readonly RosterDeliveryResourceType = RosterDeliveryResourceType;

  readonly form = new FormGroup({
    datum: new FormControl<string | null>(null),
    resourceName: new FormControl<string | null>(null),
    vonzeit: new FormControl<string | null>(null),
    biszeit: new FormControl<string | null>(null),
    bemerkung: new FormControl<string | null>(null),
    tour: new FormControl<string | null>(null),
    title: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    this.form.patchValue({
      datum: this.datum(),
      resourceName: this.resourceName(),
      vonzeit: this.vonzeit() || '08:00',
      biszeit: this.biszeit() || '17:00',
      bemerkung: this.bemerkung() || null,
      tour: this.tour() || '',
      title: this.title() || '',
    });
    this.form.get('resourceName')?.disable();
  }

  save(): void {
    if (this.form.invalid) return;
    const v = this.form.getRawValue();
    const datum = v.datum ?? this.datum();
    const vonzeitTime = (v.vonzeit ?? '00:00').substring(0, 5);
    const biszeitTime = (v.biszeit ?? '00:00').substring(0, 5);
    const biszeitDatum =
      biszeitTime <= vonzeitTime ? addDays(datum as IsoDate, 1) : datum;
    this.resolve({
      datum,
      vonzeit: `${datum}T${vonzeitTime}:00`,
      biszeit: `${biszeitDatum}T${biszeitTime}:00`,
      bemerkung: v.bemerkung ?? '',
      tour: v.tour ?? '',
      title: v.title ?? 'ohne Titel',
    });
  }

  cancel(): void {
    this.resolve('CANCEL');
  }

  delete(): void {
    this.resolve('DELETE');
  }
}
